import React, { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import Checkbox from '@mui/material/Checkbox'
import LinearProgress from '@mui/material/LinearProgress'
import { getLookUpQuestion } from '../../APIManager/LookUpManager'
import GenericModal from '../../components/GenericModal'
import { ButtonType } from '../../components/GenericButton/styles'
import DefaultModalActions from '../../components/GenericModal/DefaultModalActions'
import { black, blue, grey, shade } from '../../colors'
import Loader from '../../components/Loader'
import { useQuery } from '@tanstack/react-query'
import InputAdornment from '@mui/material/InputAdornment'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import { useTheme } from '@mui/material'
import CancelIcon from '@mui/icons-material/Cancel'

interface ILinearObject {
  option: string
  weight: number
}

interface Question {
  itemType: string
  itemId?: string
  prompt: string
  object: ILinearObject[]
  isRequired: boolean
  branchingFilters: any
  modifiedOn?: number
}

interface OnAddPayload {
  newlyAddedQuestions: Question[]
  removedQuestions: Question[]
  selectedQuestions: Question[]
  updatedSelectedIds: any[]
}

interface LookUpModalProps {
  lookUpData?: Record<string, any>
  onClose: () => void
  onAdd?: (payload: OnAddPayload) => void
  totalQuestionLimit?: number
  currentQuestionCount?: number
  ignoreLimit?: boolean
}

// debouncer to limit the number of API calls
function useDebounce<T> (value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value)
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

export const LookUpModal: React.FC<LookUpModalProps> = ({
  onClose,
  onAdd,
  totalQuestionLimit = 0,
  currentQuestionCount = 0,
  lookUpData = {},
  ignoreLimit
}) => {
  const [tab, setTab] = useState(0)
  const [search, setSearch] = useState('')
  const [focusedOnSearch, setFocuedOnSearch] = useState(false)
  const debouncedSearch = useDebounce(search, 600)
  // Get the list of already selected question IDs from lookupData
  // Only include questions that are not of type 'Title' and have a valid itemId
  let alreadySelected: string[] = []
  if (Array.isArray(lookUpData.selectedIds)) {
    alreadySelected = lookUpData.selectedIds
      .filter((q: any) => q && typeof q === 'object' && q.itemType !== 'Title' && q.itemId)
      .map((q: any) => q.itemId)
  }
  // State to keep track of currently selected question IDs in the modal
  const [selected, setSelected] = useState<string[]>(alreadySelected)
  const theme = useTheme()

  // Helper function to get the unique ID for a question
  const getQuestionId = (questions: Question, index: number): string => {
    return questions.itemId?.toString() ?? `fallback-${index}`
  }

  // Fetch questions for the lookup modal using react-query
  const { data: questions = [], isLoading: loading } = useQuery<Question[]>({
    queryKey: ['lookupQuestions', tab, debouncedSearch],
    queryFn: async () => {
      const res = await getLookUpQuestion(
        tab === 0
          ? { self_generated: true, search: debouncedSearch }
          : { school_approved: true, search: debouncedSearch }
      )
      return res.data || []
    },
    staleTime: 1000,
    retry: false
  })

  // Count of currently selected questions (for progress bar and display)
  let selectedQuestionCount = 0
  if (Array.isArray(selected)) {
    selectedQuestionCount = selected.length
  }

  // Filter questions based on search input
  const filteredQuestions = questions.filter(questions =>
    questions.prompt?.toLowerCase().includes(debouncedSearch.toLowerCase())
  )

  // Check if user can select more questions (based on limit)
  const canSelectMore = (): boolean => {
    if (ignoreLimit) return true
    const remainingSlots = totalQuestionLimit - selected.length
    return remainingSlots > 0
  }

  // Handle selecting or deselecting a question in the modal
  const handleSelect = (id: string): void => {
    if (selected?.includes(id)) {
      setSelected(prev => prev?.filter(item => item !== id)) // Deselect
    } else if (canSelectMore()) {
      setSelected(prev => [...prev, id]) // Select
    }
  }

  // Handle clicking the Add button
  // This will send the updated selection to the parent, including:
  // - newly added questions
  // - removed questions
  // - all currently selected questions
  // - the updated selectedIds array for lookupData
  const handleAdd = async (): Promise<void> => {
    if (onAdd) {
      const selectedQuestions = questions?.filter((q, index) => {
        const id = getQuestionId(q, index)
        return selected?.includes(id)
      })
      const newlyAddedQuestions = selectedQuestions?.filter(question => !(lookUpData.selectedIds?.some((q: any) => q.itemId === question.itemId)))
      const removedQuestions = lookUpData.selectedIds?.filter((q: any) => q && !selected.includes(q.itemId)) || []
      // Only keep selected questions in updatedSelectedIds
      const updatedSelectedIds = lookUpData.selectedIds?.filter((q: any) => q && selected.includes(q.itemId)) || []
      onAdd({
        newlyAddedQuestions,
        removedQuestions,
        selectedQuestions,
        updatedSelectedIds
      })
    }
    onClose()
  }

  return (
    <GenericModal
      isOpen={true}
      onClose={onClose}
      title={<Typography sx={{ fontSize: '18px', fontWeight: 600, color: blue[950] }}>Lookup Question</Typography>}
      maxWidth="sm"
      height='75vh'
      action={false}
    >
      <Box display="flex" flexDirection="column" height="100%" minHeight={0}>
        <Typography sx={{ fontSize: '16px', color: grey[850], gap: 6 }}>Use a question you have previously created</Typography>
        <TextField
          fullWidth
          placeholder="Search for your questions"
          value={search}
          onChange={e => { setSearch(e.target.value) }}
          onFocus={() => { setFocuedOnSearch(true) }}
          onBlur={() => { setFocuedOnSearch(false) }}
          sx={{ my: theme.spacing(2) }}
          InputProps={{
            style: { fontSize: 15 },
            endAdornment: (
              <InputAdornment position="end">
                {(focusedOnSearch && search.length > 0) &&
                  <Box sx={{ mr: theme.spacing(1), cursor: 'pointer' }} onMouseDown={e => { e.preventDefault() }} onClick={() => { setSearch('') }}>
                    <CancelIcon />
                  </Box>
                }
                <SearchRoundedIcon sx={{ color: blue[1100] }} />
              </InputAdornment>
            )
          }}
        />

        <Tabs value={tab} onChange={(_, v) => { setTab(v) }}>
          <Tab sx={{ fontSize: '16px', fontWeight: 600, color: grey[150] }} label="My Questions" />
          <Tab sx={{ fontSize: '16px', fontWeight: 600, color: grey[150] }} label="School Approved" />
        </Tabs>

        <Box flex={1} minHeight={0} overflow="auto" mt={theme.spacing(4)}>
          {loading
            ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <Loader open={true} />
              </Box>
              )
            : filteredQuestions.length === 0
              ? (
                <Typography color="text.secondary" align="center">No questions found.</Typography>
                )
              : (
                  filteredQuestions.map((q, index) => {
                    const id = getQuestionId(q, index)
                    const isSelected = selected.includes(id)
                    const isDisabled = !isSelected && !canSelectMore()

                    return (
                    <Box
                      key={id}
                      display="flex"
                      alignItems="flex-start"
                      border={1}
                      padding={theme.spacing(1.5)}
                      borderColor={isSelected ? blue[400] : shade[650]}
                      borderRadius={theme.spacing(3)}
                      mb={theme.spacing(2)}
                      mr={theme.spacing(3)}
                      sx={{
                        backgroundColor: grey[0],
                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                        '&:hover': { boxShadow: '0 2px 4px rgba(0,0,0,0.1)' },
                        opacity: isDisabled ? 0.6 : 1
                      }}
                    >
                      <Checkbox
                        sx={{ color: grey[350] }}
                        checked={isSelected}
                        disabled={isDisabled}
                        onChange={() => { handleSelect(id) }}
                      />
                      <Box mt={theme.spacing(2)}>
                        <Typography fontSize={'16px'} fontWeight={500} color={blue[1100]} paddingBottom={theme.spacing(2)}>
                          {q.prompt ?? ''}
                        </Typography>
                        <Typography fontSize={'16px'} color={grey[150]} paddingBottom={theme.spacing(4)}>
                          {q.itemType.replace(/([a-z])([A-Z])/g, '$1 $2')}
                        </Typography>
                        <Typography fontSize={'16px'} fontWeight={500} color={grey[150]}>
                          Last Modified: {q.modifiedOn ? new Date(q.modifiedOn).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }) : '-'}
                        </Typography>
                      </Box>
                    </Box>
                    )
                  })
                )}
        </Box>

        <Box display="flex" alignItems="end" justifyContent="space-between" mt={theme.spacing(1)}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '50%' }}>
            {!ignoreLimit && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                <LinearProgress
                  variant="determinate"
                  value={selectedQuestionCount / totalQuestionLimit * 100}
                  sx={{
                    width: 60,
                    height: 8,
                    borderRadius: theme.spacing(2),
                    bgcolor: grey[200]
                  }}
                />
                <Typography sx={{ fontStyle: 'italic', color: black[100], fontSize: '12px' }}>
                  {`${selectedQuestionCount} out of ${totalQuestionLimit} questions selected`}
                </Typography>
              </Box>
            )}
          </Box>

          <DefaultModalActions
            confirmButtonProps={{
              type: 'submit',
              text: 'Add',
              buttonType: ButtonType.Primary,
              disabled: selected?.length === 0,
              onClick: handleAdd
            }}
            destructiveButtonProps={{
              text: 'Cancel',
              onClick: onClose
            }}
          />
        </Box>
      </Box>
    </GenericModal>
  )
}
